/**
 * TAMIX SAAS PLATFORM - Authentication Store
 * ==========================================
 * Generated: 2025-09-22 - Session 6 Sprint 0
 * Zustand store for authentication state management
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User } from '@/types/auth'
import { authApi } from '@/services/authApi'

interface AuthState {
  // State
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  // Actions
  login: (token: string, user: User) => void
  logout: () => void
  clearError: () => void
  setLoading: (loading: boolean) => void
  setError: (error: string) => void
  
  // API Actions
  loginWithCredentials: (email: string, password: string) => Promise<void>
  registerUser: (userData: {
    email: string
    password: string
    first_name: string
    last_name: string
    phone?: string
    job_title?: string
    tenant_id?: string
  }) => Promise<void>
  loginWithGoogle: () => Promise<void>
  refreshToken: () => Promise<void>
  getCurrentUser: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial State
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      login: (token: string, user: User) => {
        set({
          token,
          user,
          isAuthenticated: true,
          error: null,
        })
      },

      logout: () => {
        set({
          token: null,
          user: null,
          isAuthenticated: false,
          error: null,
        })
        // Clear API token
        authApi.clearToken()
      },

      clearError: () => set({ error: null }),

      setLoading: (isLoading: boolean) => set({ isLoading }),

      setError: (error: string) => set({ error, isLoading: false }),

      // API Actions
      loginWithCredentials: async (email: string, password: string) => {
        try {
          set({ isLoading: true, error: null })
          const response = await authApi.loginWithCredentials({ email, password })
          set({
            token: response.access_token,
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
        } catch (error) {
          const msg = error instanceof Error ? error.message : 'Error al iniciar sesión'
          set({
            error: msg,
            isLoading: false,
          })
          throw error
        }
      },

      registerUser: async (userData) => {
        try {
          set({ isLoading: true, error: null })
          await authApi.register(userData)
          // Automatically login after successful registration
          const response = await authApi.loginWithCredentials({
            email: userData.email,
            password: userData.password,
          })
          set({
            token: response.access_token,
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
        } catch (error) {
          const msg = error instanceof Error ? error.message : 'Error en el registro'
          set({
            error: msg,
            isLoading: false,
          })
          throw error
        }
      },

      loginWithGoogle: async () => {
        try {
          set({ isLoading: true, error: null })
          
          // Redirect to Google OAuth
          const loginUrl = await authApi.getGoogleLoginUrl()
          window.location.href = loginUrl
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Login failed',
            isLoading: false,
          })
        }
      },

      refreshToken: async () => {
        try {
          const { token } = get()
          if (!token) {
            throw new Error('No token available')
          }

          const response = await authApi.refreshToken(token)
          set({
            token: response.access_token,
            user: response.user,
            isAuthenticated: true,
          })
        } catch (error) {
          // Token refresh failed, logout user
          get().logout()
          throw error
        }
      },

      getCurrentUser: async () => {
        try {
          const { token } = get()
          if (!token) {
            throw new Error('No token available')
          }

          const user = await authApi.getCurrentUser()
          set({ user })
        } catch (error) {
          console.error('Failed to get current user:', error)
          // Don't logout on user fetch failure
        }
      },
    }),
    {
      name: 'tamix-auth',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)

// Auto-refresh token on app load
if (typeof window !== 'undefined') {
  const { token, refreshToken } = useAuthStore.getState()
  if (token) {
    refreshToken().catch(() => {
      // Ignore refresh errors on initial load
    })
  }
}